<template>
  <v-form ref="form" @submit.prevent>
    <FormSection>
      <BaseSelect
        v-model="formData.calculationDesire"
        class="mb-6"
        :error-messages="calculationDesireErrors"
        :hide-details="!calculationDesireErrors.length"
        :label="$t('complaintForm.ticket.engine.calculation_wish')"
        :items="calculationWishSelectItems"
        required
        v-on="getTouchListeners('formData.calculationDesire')"
      />

      <BaseAlert
        v-if="typeOfProcessingInfo[0]"
        type="error"
        icon="warning"
        :message="typeOfProcessingInfo[0]"
      >
        <template #message>
          <p v-if="isTransportationDamageType" class="mb-0" :class="$style['transport-evaluation']">
            <span>{{ $t('complaintForm.transportEvaluation.yourExperience') }} </span>
            <span>
              <a :href="$t('complaintForm.transportEvaluation.surveyLink')" target="_blank"
                >{{ $t('complaintForm.transportEvaluation.surveyText') }}
              </a>
            </span>
            <span>{{ $t('complaintForm.transportEvaluation.yourFeedback') }} </span>
          </p>
          <p
            v-else-if="isWarrantyAndGuaranteeType"
            class="mb-0"
            :class="$style['transport-evaluation']"
          >
            <a
              :href="$t('complaintForm.components.processinginfo.warrantyAndGuarantee.link')"
              target="_blank"
              >{{ $t('complaintForm.components.processinginfo.warrantyAndGuarantee.linkText') }}
            </a>
          </p>
        </template>
      </BaseAlert>
    </FormSection>

    <FormSection
      v-if="isTransportationDamageType || isWarrantyAndGuaranteeType || isProductLiabilityType"
      element-type="div"
    >
      <FormSection>
        <!-- TODO: UPDATE (https://docs.google.com/spreadsheets/d/1CbT1j1d7aHwhHRH3x7f4oWtAoQh2n00mlCuKkjFiECI/edit#gid=0) -->

        <AztecCameraScanner @code_autofill="updateAztec" />

        <BaseTextfield
          v-model="serialNumberBike"
          v-mask="'###### ####'"
          :class="[
            serialNumberBikeLoading || isIncorrectSerialNumber || isCorrectSerialNumber
              ? [$style['serial-number-box']]
              : 'mb-6',
          ]"
          :error-messages="serialNumberBikeErrors"
          :hide-details="!serialNumberBikeErrors.length"
          :label="$t('complaintForm.ticket.engine.serial_number')"
          required
          v-on="getTouchListeners('serialNumberBike')"
        />

        <div
          v-if="serialNumberBikeLoading"
          :class="$style['serial-number-container']"
          class="mt-1 mb-4 align-center d-flex"
        >
          <v-progress-circular
            :class="$style.spinner"
            :size="16"
            width="2"
            color="primary"
            :indeterminate="!!serialNumberBikeLoading"
          />
          <p :class="[$style['serial-number'], 'mb-0']">
            {{ serialNumberBikeLoading }} {{ $t('quickbuy.loading') }}
          </p>
        </div>
        <div
          v-if="!serialNumberBikeLoading && (isCorrectSerialNumber || isIncorrectSerialNumber)"
          :class="$style['serial-number-container']"
        >
          <IconSelect
            :icon="isCorrectSerialNumber ? 'checkmark' : 'info'"
            height="44"
            width="44"
            :class="[
              $style['serial-number-icon'],
              isCorrectSerialNumber ? $style['correct-icon'] : $style['incorrect-icon'],
            ]"
          />
          <p
            :class="[
              $style['serial-number'],
              isCorrectSerialNumber
                ? $style['serial-number-correct']
                : $style['serial-number-incorrect'],
            ]"
          >
            <span v-if="isCorrectSerialNumber">{{
              $t('complaintForm.ticket.error.valid-serial-number')
            }}</span>
            {{
              isCorrectSerialNumber
                ? bikeName
                : $t('complaintForm.ticket.error.invalid-serial-number')
            }}
          </p>
        </div>

        <BaseAlert
          type="warning"
          icon="info"
          :message="$t('complaintForm.ticket.engine.explain_serial')"
        >
          <v-img
            v-if="imageData.serial"
            :min-height="190"
            :max-height="190"
            :src="imageData.serial.imageUrl"
            :lazy-src="imageData.serial.imageUrl"
            :alt="imageData.serial.name"
            cover
            position="top center"
          />
        </BaseAlert>
      </FormSection>

      <FormSection>
        <v-row>
          <v-col class="py-0" cols="12" md="6" sm="12">
            <BaseHeading :level="3" class="mb-1 required">{{
              $t('complaintForm.ticket.engine.sale_date')
            }}</BaseHeading>

            <BaseDatepicker
              v-model="formData.saleDate"
              class="mb-6"
              :error-messages="saleDateErrors"
              :hide-details="!saleDateErrors.length"
              :locale="language"
              :show-current="false"
              :max="currentDate"
              required
              full-width
              landscape
              v-on="getTouchListeners('formData.saleDate')"
            />
          </v-col>

          <v-col class="py-0" cols="12" md="6" sm="12">
            <BaseHeading v-if="!isTransportationDamageType" :level="3" class="mb-1 required">{{
              $t('complaintForm.ticket.engine.purchase_receipt')
            }}</BaseHeading>

            <BaseHeading v-if="isTransportationDamageType" :level="3" class="mb-1 required">
              {{ $t('complaintForm.ticket.battery.purchase_receipt') }}
            </BaseHeading>

            <FileDropzone
              v-model="proofOfPurchase"
              class="mb-6"
              :error-messages="fileUploadErrors"
              :hide-details="!fileUploadErrors.length"
              url="/api/bureturnapi?code=image-upload"
              required
              @[onDropzoneSuccess]="setFileUploadPath"
              @[onDropzoneSending]="setPending(true)"
              @[onDropzoneComplete]="setPending(false)"
            />
          </v-col>
        </v-row>
      </FormSection>

      <FormSection v-if="isProductLiabilityType">
        <v-row>
          <v-col class="py-0" cols="12" md="6" sm="12">
            <BaseHeading :level="3" class="mb-1 required">{{
              $t('complaintForm.ticket.component.accident_date')
            }}</BaseHeading>

            <BaseDatepicker
              v-model="formData.accidentDate"
              class="mb-6"
              :error-messages="accidentDateErrors"
              :hide-details="!accidentDateErrors.length"
              :locale="language"
              :show-current="false"
              :max="currentDate"
              required
              full-width
              landscape
              v-on="getTouchListeners('formData.accidentDate')"
            />
          </v-col>

          <v-col class="py-0 pt-md-8" cols="12" md="6" sm="12">
            <BaseTextarea
              v-model="formData.accidentReport"
              rows="15"
              :error-messages="accidentReportErrors"
              :hide-details="!accidentReportErrors.length"
              :label="$t('complaintForm.ticket.component.accident_report')"
              counter
              required
              v-on="getTouchListeners('formData.accidentReport')"
            />
          </v-col>
        </v-row>
      </FormSection>

      <FormSection v-if="isTransportationDamageType">
        <BaseTextfield
          v-model="formData.deliveryNoteNo"
          class="mb-6"
          :error-messages="deliveryNoteNoErrors"
          :hide-details="!deliveryNoteNoErrors.length"
          :label="$t('complaintForm.ticket.components.delivery_note_no')"
          required
          v-on="getTouchListeners('formData.deliveryNoteNo')"
        />

        <BaseAlert
          v-if="typeOfProcessingInfo[1]"
          type="error"
          icon="warning"
          :message="typeOfProcessingInfo[1]"
        />
      </FormSection>
    </FormSection>

    <FormSection v-if="!canBeClaimed">
      <BaseAlert type="warning" icon="info" :message="$t('complaintForm.claimNotPossible')" />
    </FormSection>

    <ComplaintFormStepperControls
      :is-valid="isValid"
      :has-required-fields="true"
      :prev-step="prevStep"
      :next-step="submit"
    />
  </v-form>
</template>

<script>
import { compareAsc, subYears, parseISO } from 'date-fns'
import { get, replace, compact, isEmpty, debounce } from 'lodash-es'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { mask } from 'vue-the-mask'
import { required, numeric, minLength, maxLength, requiredIf } from 'vuelidate/lib/validators'

import ComplaintFormImageDataMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-image-data-mixin.js'

import StepperMixin from '@/js/mixins/stepper-mixin.js'
import FormValidationMixin from '@/js/components/Forms/mixins/form-validation-mixin.js'
import ComplaintFormMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-default-mixin.js'

import FORM_CONSTANTS from '@/js/components/Forms/ComplaintForm/_store-module/constants.js'
import FORM_MUTATION from '@/js/components/Forms/ComplaintForm/_store-module/mutation-types.js'
import FORM_ACTION from '@/js/components/Forms/ComplaintForm/_store-module/action-types.js'
import FORM_GETTER from '@/js/components/Forms/ComplaintForm/_store-module/getter-types.js'

import FormSection from '@/js/components/Forms/FormSection.vue'
import FileDropzone, { FILE_DROPZONE_EVENTS } from '@/js/components/Shared/FileDropzone.vue'
import AztecCameraScanner from '@/js/components/Zxing/AztecCameraScanner.vue'

import ComplaintFormStepperControls from '../../ComplaintFormStepperControls.vue'
import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'

const serialNumberBikeMaxLength = 10
const serialNumberBikeMinLength = 9

export default {
  name: 'ComplaintFormComponentProductData',
  components: {
    FormSection,
    ComplaintFormStepperControls,
    FileDropzone,
    IconSelect,
    AztecCameraScanner,
  },
  directives: {
    mask,
  },
  mixins: [FormValidationMixin, ComplaintFormMixin, ComplaintFormImageDataMixin],
  props: {
    ...StepperMixin.props,
  },
  data() {
    return {
      // Constants
      onDropzoneSuccess: FILE_DROPZONE_EVENTS.VDROPZONE_SUCCESS,
      onDropzoneSending: FILE_DROPZONE_EVENTS.VDROPZONE_SENDING,
      onDropzoneComplete: FILE_DROPZONE_EVENTS.VDROPZONE_COMPLETE,
      formData: {
        calculationDesire: null,
        deliveryNoteNo: null,
        saleDate: null,
        fileUpload: '',
        accidentDate: null,
        accidentReport: null,
      },
      // Other data
      currentDate: new Date().toISOString().substr(0, 10),
      proofOfPurchase: null,
      proofOfPurchaseError: '',
      serialNumberBike: null,
      serialNumberBikeLoading: false,
      // validation values
      serialNumberBikeMaxLength,
      serialNumberBikeMinLength,
      // Bike serial number validation
      isIncorrectSerialNumber: false,
      isCorrectSerialNumber: undefined,
      bikeName: '',
      requestedBikeSerialnumber: undefined,
    }
  },
  validations: {
    serialNumberBike: {
      required,
      numeric: {
        value(val) {
          return numeric(replace(val, ' ', ''))
        },
      },
      minLength: minLength(serialNumberBikeMinLength + 1), // +1 because of space
      maxLength: maxLength(serialNumberBikeMaxLength + 1), // +1 because of space
    },
    formData: {
      calculationDesire: {
        required,
      },
      deliveryNoteNo: {
        required: requiredIf(function () {
          return this.isTransportationDamageType
        }),
      },
      saleDate: {
        required,
      },
      fileUpload: {
        required,
      },
      accidentDate: {
        required: requiredIf(function () {
          return this.isProductLiabilityType
        }),
      },
      accidentReport: {
        required: requiredIf(function () {
          return this.isProductLiabilityType
        }),
      },
    },
  },
  computed: {
    ...mapGetters(FORM_CONSTANTS.STORE_ID, {
      language: FORM_GETTER.LANGUAGE,
    }),

    /** Computed helper */
    serialNo1() {
      return get(this.serialNumberBike?.split(' '), '0', '')
    },
    serialNo2() {
      return get(this.serialNumberBike?.split(' '), '1', '')
    },
    isWarrantyAndGuaranteeType() {
      return (
        this.formData.calculationDesire ===
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_WARRANTY_AND_GUARANTEE
      )
    },
    isProductLiabilityType() {
      return (
        this.formData.calculationDesire ===
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_PRODUCT_LIABILITY
      )
    },
    isTransportationDamageType() {
      return (
        this.formData.calculationDesire ===
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_TRANSPORTATION_DAMAGE
      )
    },
    typeOfProcessingInfo() {
      const typeOfProcessingInfo = []

      if (this.isWarrantyAndGuaranteeType) {
        typeOfProcessingInfo.push(this.$t('complaintForm.claim.type_of_processing1_1'))
      }

      if (this.isProductLiabilityType) {
        typeOfProcessingInfo.push(this.$t('complaintForm.claim.type_of_processing2_1'))
      }

      if (this.isTransportationDamageType) {
        typeOfProcessingInfo.push(
          this.$t('complaintForm.claim.type_of_processing3_1'),
          this.$t('complaintForm.claim.type_of_processing3_2'),
        )
      }

      return typeOfProcessingInfo
    },
    canBeClaimed() {
      return this.formData.saleDate
        ? compareAsc(new Date(this.formData.saleDate), subYears(parseISO(this.currentDate), 3)) >= 0
        : true
    },

    /** Form data */
    fullFormData() {
      return {
        ...this.formData,
        serialNo1: this.serialNo1,
        serialNo2: this.serialNo2,
      }
    },

    /** Select items */
    calculationWishSelectItems() {
      return [
        {
          text: this.$t('complaintForm.claim.type_of_processing.1'),
          value: FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_WARRANTY_AND_GUARANTEE,
        },
        {
          text: this.$t('complaintForm.claim.type_of_processing.2'),
          value: FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_PRODUCT_LIABILITY,
        },
        {
          text: this.$t('complaintForm.claim.type_of_processing.3'),
          value: FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_TRANSPORTATION_DAMAGE,
        },
      ]
    },

    /** Errors */
    calculationDesireErrors() {
      return this.formErrorHelper('formData.calculationDesire', [this.chooseError])
    },
    deliveryNoteNoErrors() {
      return this.formErrorHelper('formData.deliveryNoteNo', [this.requiredError])
    },
    serialNumberBikeErrors() {
      return this.formErrorHelper('serialNumberBike', [
        this.requiredError,
        this.numericError,
        this.minLengthError(this.serialNumberBikeMinLength),
        this.maxLengthError(this.serialNumberBikeMaxLength),
      ])
    },

    saleDateErrors() {
      return this.formErrorHelper('formData.saleDate', [this.requiredError])
    },
    fileUploadErrors() {
      return compact([
        this.proofOfPurchaseError,
        ...this.formErrorHelper('formData.fileUpload', [
          {
            condition: 'required',
            error: this.fileErrorMessage,
          },
        ]),
      ])
    },
    accidentDateErrors() {
      return this.formErrorHelper('formData.accidentDate', [this.requiredError])
    },
    accidentReportErrors() {
      return this.formErrorHelper('formData.accidentReport', [this.requiredError])
    },
  },
  watch: {
    'formData.calculationDesire'(newValue, oldValue) {
      if (oldValue === newValue) {
        return
      }

      if (this.isFeeBasedType) {
        this.proofOfPurchase = null
        this.formData.saleDate = null
        this.formData.fileUpload = null
        this.formData.accidentDate = null
        this.formData.accidentReport = null
      }

      if (this.isWarrantyAndGuaranteeType) {
        this.formData.accidentDate = null
        this.formData.accidentReport = null
      }
    },
    proofOfPurchase(newFiles, oldFiles) {
      if (oldFiles === newFiles) {
        return
      }

      if (this.proofOfPurchase?.length === 0) {
        this.proofOfPurchaseError = ''
        this.formData.fileUpload = ''
      }
    },

    serialNumberBike: {
      handler: debounce(function () {
        const newValueTrimmed = this.serialNumberBike.split(' ').join('')
        if (
          newValueTrimmed.length <= this.serialNumberBikeMaxLength &&
          newValueTrimmed.length >= this.serialNumberBikeMinLength &&
          this.requestedBikeSerialnumber !== newValueTrimmed
        ) {
          this.requestData(newValueTrimmed)
        }
      }, 500),
    },
  },

  methods: {
    ...mapActions(FORM_CONSTANTS.STORE_ID, {
      getComponentData: FORM_ACTION.GET_BIKE_COMPONENT_DATA,
    }),
    ...mapMutations(FORM_CONSTANTS.STORE_ID, {
      updateFormData: FORM_MUTATION.UPDATE_FORM_DATA,
      setPending: FORM_MUTATION.SET_FORM_PENDING,
    }),
    async requestData(serialnumber) {
      this.serialNumberBikeLoading = serialnumber
      this.requestedBikeSerialnumber = serialnumber
      const data = await this.getComponentData(serialnumber)
      // this.setPending(false)
      this.serialNumberBikeLoading = false
      this.isCorrectSerialNumber = !isEmpty(data)
      this.isIncorrectSerialNumber = isEmpty(data)
      this.bikeName = get(data, ['ComponentData', 'Bike', 'ItemDesc'])
    },
    updateAztec(sn) {
      this.serialNumberBike = sn
    },
    submitCallback() {
      this.updateFormData(this.fullFormData)
      this.nextStep()
    },
    setFileUploadPath({ response }) {
      const fileUploadPath = get(response, 'files.0', '')
      this.formData.fileUpload = get(response, 'files.0', '')

      if (!fileUploadPath) {
        const fileUploadError = get(response, 'error')

        if (fileUploadError) {
          this.proofOfPurchaseError = this.$t('form.dropzone.uploadFailed')
        }
      }
    },
  },
}
</script>

<style lang="scss" module>
.serial-number-box {
  margin-bottom: 0 !important;
}

.serial-number-container {
  display: flex;
  padding: 0 0.75rem;
}

.serial-number {
  padding: 0 0.32rem;

  font-size: var(--font-sm) !important;
}

.serial-number-incorrect {
  color: var(--black);
}

.serial-number-correct {
  color: var(--primary) !important;
}

.serial-number-icon {
  width: 0.95rem;
  height: 0.95rem;
  margin-top: 0.2rem;

  border-radius: 50%;
}

.correct-icon {
  color: var(--success);
}

.incorrect-icon {
  color: var(--warning);
}

.transport-evaluation {
  margin-top: 16px;

  color: black;
}
</style>
